import React, { useEffect } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { RefreshCw, AlertTriangle, FileText } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import LogFilters from "./LogFilters";
import LogListContent from "./LogListContent";

export default function ServerLogsDialog({
  open,
  onOpenChange,
  selectedServerForLogs,
  serverLogs,
  loadingLogs,
  logStatusFilter,
  setLogStatusFilter,
  logStartDate,
  setLogStartDate,
  logEndDate,
  setLogEndDate,
  onFetchLogs,
  translateLogStatus,
}) {
  const { t } = useLanguage();

  // Refetch logs when date filters change
  useEffect(() => {
    if (selectedServerForLogs && open) {
      onFetchLogs(selectedServerForLogs);
    }
  }, [logStartDate, logEndDate]);

  const handleReset = () => {
    setLogStartDate("");
    setLogEndDate("");
    setLogStatusFilter("ALL");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 break-words">
            <FileText className="h-5 w-5" />
            {t("serverLogsTitle").replace("{name}", selectedServerForLogs?.name || "")}
          </DialogTitle>
          <DialogDescription>{t("viewServerLogsDescription")}</DialogDescription>
        </DialogHeader>

        {loadingLogs ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
            <span className="ml-2 text-slate-500">{t("loadingLogs")}</span>
          </div>
        ) : serverLogs?.error ? (
          <div className="py-8 text-center text-red-600">
            <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
            <p>{serverLogs.error}</p>
          </div>
        ) : serverLogs ? (
          <div className="space-y-6">
            {/* Server Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs text-slate-500 mb-1">{t("serverStarted")}</p>
                <p className="font-semibold text-sm">
                  {serverLogs.serverStartTime
                    ? format(new Date(serverLogs.serverStartTime), "PPpp")
                    : t("na")}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">{t("databaseConnectionEstablished")}</p>
                <p className="font-semibold text-sm">
                  {serverLogs.databaseConnectionTime
                    ? format(new Date(serverLogs.databaseConnectionTime), "PPpp")
                    : t("na")}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">{t("currentMemoryUsage")}</p>
                <p className="font-semibold text-sm">
                  {serverLogs.currentMemoryUsage !== null && serverLogs.currentMemoryUsage !== undefined
                    ? `${serverLogs.currentMemoryUsage}%`
                    : t("na")}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">{t("totalLogEntries")}</p>
                <p className="font-semibold text-sm">{serverLogs.logs?.length || 0}</p>
              </div>
            </div>

            {/* Log Entries */}
            <div className="space-y-4">
              {/* Filters */}
              <LogFilters
                logStatusFilter={logStatusFilter}
                setLogStatusFilter={setLogStatusFilter}
                logStartDate={logStartDate}
                setLogStartDate={setLogStartDate}
                logEndDate={logEndDate}
                setLogEndDate={setLogEndDate}
                onReset={handleReset}
              />

              {/* Log List */}
              <LogListContent
                serverLogs={serverLogs}
                selectedServerForLogs={selectedServerForLogs}
                logStatusFilter={logStatusFilter}
                logStartDate={logStartDate}
                logEndDate={logEndDate}
                translateLogStatus={translateLogStatus}
              />
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
